import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ChevronRight, Folder } from 'lucide-react';
import { backend } from '@/api/backend';
import { explainLine } from '@/api/explain';
import { ApiError } from '@/api/http';
import { Panel, PanelHeader, Skeleton, ReadFailed } from '@/components/ui';
import { ToolIcon } from '@/components/ToolIcon';
import { formatBytes, formatCount } from '@/lib/format';
import { WorkOnFolder } from '@/components/WorkOnFolder';
import {
  buildCollectionTree,
  collectionLabel,
  coverage,
  namesASystem,
  rootsByWeight,
  type CollectionNode,
} from '@/pages/collections';

const retry = (n: number, e: unknown) => (e instanceof ApiError ? e.retryable && n < 2 : n < 2);

/**
 * Where the files came from, as the tenant filed them.
 *
 * This is NOT the tool badge and must never be read as one. A file inside a
 * GitHub export is still an npm manifest; what produced it comes from its own
 * bytes and is answered elsewhere. The backend serves the two from separate
 * endpoints so they cannot be confused, and nothing here touches attribution.
 *
 * It earns its place because 84.6% of tenant objects carry no tool and mostly
 * never will - a `.ts` file inside a repository export has no fingerprint of
 * the system it came from. On capway that was 20,341 rows of "unknown" and is
 * one line here saying GitHub.
 *
 * Collapsed by default, and that is the whole point. capway files 4,319
 * collections eighteen levels deep; drawing them open would rebuild the wall
 * of rows this replaces.
 */
export function SourceCollections({ bucketId, scope }: {
  bucketId: string;
  /** The scope these folders sit in, so one of them can become its own. */
  scope?: { name: string; accountId: string | null; region: string | null };
}) {
  const q = useQuery({
    queryKey: ['source-collections', bucketId],
    queryFn: ({ signal }) => backend.listSourceCollections(bucketId, signal),
    retry,
    staleTime: 60_000,
  });

  if (q.isPending) return <Skeleton className="mx-4 my-3 h-5" />;

  if (q.isError) {
    return (
      <Panel>
        <PanelHeader title="Where these files came from" />
        <ReadFailed what={explainLine(q.error, 'read where these files came from')} onRetry={() => void q.refetch()} />
      </Panel>
    );
  }

  const roots = rootsByWeight(buildCollectionTree(q.data.items));

  // A bucket whose files sit at the root is filed under nothing. That is an
  // answer and not a failure, so it does not get an error state.
  if (roots.length === 0) {
    return (
      <Panel>
        <PanelHeader title="Where these files came from" />
        <p className="px-4 py-3 text-small text-ink-3">
          Nothing here is filed in folders, so there is no filing to read. This appears on its own after a sync run;
          there is nothing to start.
        </p>
      </Panel>
    );
  }

  const { filed, collections } = coverage(roots);
  return (
    <Panel>
      <PanelHeader
        title="Where these files came from"
        sub={`${formatCount(filed)} files across ${formatCount(collections)} folders · read from the tenant's own filing, not from the bytes`}
      />
      {/* The easy path, said once at the top of the list rather than left to
          be discovered from a button on a row. Registering the bucket and then
          promoting a folder is the whole workflow: there is no delimiter
          listing for a bucket nobody has walked, so the tenant's own filing is
          both the first sight of the folders and the way to pick one. */}
      {scope && (
        <p className="border-b border-line px-4 py-2 text-caption text-ink-3">
          Working on just one part of this? Open a folder and choose{' '}
          <span className="text-ink-2">work on this</span> — it becomes its own scope, with its own walk, package and
          handover, and it will not see the rest of the bucket.
        </p>
      )}
      {roots.map((node) => (
        <Row key={node.item.id} node={node} depth={0} scope={scope} />
      ))}
      {/* Said once, at the bottom, because the distinction is the entire
          reason this panel is separate from the tool badge. */}
      <p className="border-t border-line px-4 py-2 text-caption text-ink-4">
        {q.data.basis}
      </p>
    </Panel>
  );
}

/** One folder, and its children once opened. */
function Row({ node, depth, scope }: {
  node: CollectionNode;
  depth: number;
  scope?: { name: string; accountId: string | null; region: string | null };
}) {
  const [open, setOpen] = useState(false);
  const { item, children } = node;
  const named = namesASystem(item);
  const hasChildren = children.length > 0;

  return (
    <>
      <div
        className="flex items-center gap-2 border-b border-line px-4 py-1.5 text-small last:border-0"
        style={{ paddingLeft: `${16 + depth * 16}px` }}
      >
        {hasChildren ? (
          <button
            type="button"
            onClick={() => setOpen((o) => !o)}
            aria-expanded={open}
            className="flex min-w-0 flex-1 items-center gap-2 text-left transition-colors duration-fast hover:text-ink"
          >
            <ChevronRight
              className={`h-3 w-3 flex-none text-ink-4 transition-transform duration-fast ${open ? 'rotate-90' : ''}`}
              aria-hidden="true"
            />
            <Name item={item} named={named} />
          </button>
        ) : (
          <span className="flex min-w-0 flex-1 items-center gap-2 pl-5">
            <Name item={item} named={named} />
          </span>
        )}
        {/* Everything beneath, not the direct children. Summing the children
            of a node would count every file in its subtree twice. */}
        <span className="mono flex-none text-caption text-ink-3">{formatCount(item.memberCount)} files</span>
        <span className="mono w-20 flex-none text-right text-caption text-ink-4">{formatBytes(item.byteTotal)}</span>
        {/* A folder is a scope the moment someone asks for it: same account,
            same bucket, this prefix. It gets its own discovery, walk, package
            and handover, and cannot see the rest of the bucket. */}
        {scope && <WorkOnFolder scope={scope} prefix={item.pathPrefix} />}
      </div>
      {open && children.map((child) => <Row key={child.item.id} node={child} depth={depth + 1} scope={scope} />)}
    </>
  );
}

/**
 * The name, and the one rule that matters.
 *
 * `toolId: null` is an answer. A folder called `Corpus` names no product, and
 * the API refuses to guess because a wrong system is worse than none. So an
 * unnamed collection is drawn as a folder with its own name, never as "unknown
 * system" and never as a warning. On capway that is 4,316 of 4,319 rows.
 */
function Name({ item, named }: { item: CollectionNode['item']; named: boolean }) {
  return (
    <>
      {named ? (
        <ToolIcon tool={item.toolId!} />
      ) : (
        <Folder className="h-3.5 w-3.5 flex-none text-ink-4" aria-hidden="true" />
      )}
      <span className="min-w-0 flex-1 truncate">
        <span className={named ? 'text-ink' : 'mono text-ink-2'}>{collectionLabel(item)}</span>
        {named && <span className="mono ml-2 text-caption text-ink-4">{item.segment}</span>}
      </span>
    </>
  );
}
